const http = require('core/http');
const Q = require('q');
const beatriceDataMap = require('beatrice-data-map');
const definitions = require('../../../../../../lib/services/api/core/apiDefinitions').services;
const serviceRequestValidator = require('../../../../../../lib/services/api/core/serviceRequestValidator');
const cache = require('cache');
const ENDPOINT = '/v2/';
const userContext = require('core/userContext');
const ERRORS = require('core/errorMessages');
const session = require('core/session');
const page = require('page');
const logger = require('utilities/logger');
const utilities = require('utilities');
const alertUtilities = require('utilities/alertUtilities');
const campaignUtilities = require('utilities/campaignUtilities');
const getCampaignId = campaignUtilities.getCampaignId;
const getSource = campaignUtilities.getSource;
const getCategory = campaignUtilities.getCategory;
const model = require('model/model');
const pubsub = require('events/pubsub');

const VERSION = window.readworks.version || 0;
const GENERIC_ERROR_MESSAGE = alertUtilities.messages.GENERIC_ERROR_MESSAGE;
const MISSING_REQUIRED_FIELDS = 'All required fields are not present';
const EXTRA_PARAMS = 'All params are not in definition.fields';
const METHOD_MISMATCH = 'Method does not match definition';
const MISSING_BODY = 'No body for request with required fields';
const INVALID_PARAMS = 'Parameters are out of spec';

// API Definition uses "delete"
const methodConversion = {
	get: 'get',
	post: 'post',
	put: 'put',
	delete: 'del'
};

function requester(method, endpoint, data) {
	let definition = this.definition ? this.definition[endpoint] : null;
	let spec = definition ? definition.spec : null;
	let fields = definition ? definition.fields : null;
	let requiredFields = definition ? definition.reqFields : null;
	let enforcedMethod = definition && definition.method ? methodConversion[definition.method] : null;
	let url = ENDPOINT + this.baseURL + (endpoint ? '/' + endpoint : '');
	let info = [method.toUpperCase(), url, JSON.stringify(data)];
	let params = Object.keys(data || {});
	let fatalError = false;
	let campaignId = getCampaignId();
	let source = getSource();
	let category = getCategory();

	if (this.baseURL === 'rest') {
		info.unshift('No Service Definition for rest endpoints');
	} else if (!definition) {
		info.unshift('No Service Definition');
	}

	if (spec) {
		let validationError = serviceRequestValidator(definition, data);
		if (validationError) {
			fatalError = true;
			if (validationError.r && validationError.r.length) {
				info.unshift(MISSING_REQUIRED_FIELDS);
			} else if (validationError.i && validationError.i.length) {
				info.unshift(INVALID_PARAMS);
			} else if (validationError.n && validationError.n.length) {
				info.unshift(EXTRA_PARAMS);
			}
		}
	} else {
		if (requiredFields) {
			if (!data || typeof data !== 'object') {
				fatalError = true;
				info.unshift(MISSING_BODY);
			} else {
				// All required fields must be present in data
				let requiredKeys = Object.keys(requiredFields);
				let allRequiredFieldsPresent = requiredKeys.every(p => {
					let val = data[p];
					return val !== null && typeof val !== 'undefined';
				});
				if (!allRequiredFieldsPresent) {
					fatalError = true;
					info.unshift(MISSING_REQUIRED_FIELDS);
				}
			}
		}

		// Check that all fields in data body are in definition.fields
		if (fields && params.length) {
			let allParamsInFields = params.every(p => fields[p]);

			// Possible trash in request
			if (!allParamsInFields) {
				fatalError = true;
				info.unshift(EXTRA_PARAMS);
			}
		}

		if (enforcedMethod && enforcedMethod.toLowerCase() !== method.toLowerCase()) {
			fatalError = true;
			info.unshift(METHOD_MISMATCH);
		}
	}

	if (fatalError) {
		logger.error(...info);
		// reject promise
		return Q().then(() => {
			alertUtilities.error(GENERIC_ERROR_MESSAGE);
			throw new Error(info[0]);
		});
	}

	let headers = {
		'x-auth': userContext.getToken(),
		'x-rw-version': VERSION,
		'x-rw-session': session.getId()
	};

	if (campaignId) {
		headers['x-campaign-id'] = campaignId;
	}

	if (source) {
		headers['x-source'] = source;
	}

	if (category) {
		headers['x-category'] = category;
	}

	return http[method]
		.call(http, url, data, headers)
		.then(function (response) {
			// Maintenance page went up
			if (response.headers['x-amz-meta-mode'] === 'maintenance') {
				location.reload(true);
			} else {
				return response.body;
			}
			return null;
		})
		.fail(function (error) {
			// a client/server version mismatch occurred, so let's try and force a reload
			if (error.status === 409 && error.code === 1076) {
				location.reload(true);

				return;
			}

			if (error.status === 409) {
				if (userContext.isStudent()) {
					switch (error.code) {
						case 1068: // Attempted to mutate a workspace for a deleted assignment, kick back to home
						case 1075: // Attempted to access a different class's record (likely via bookmark), kick back to home
						case 401:
						case 1030:
						case 1095:
							page('/student');
							break;
						case 1063:
							// Attempted to use expired Google token, kick back to login
							page('/student-authentication');
							model.remove('localGCAccessToken');
							break;
						case 1031: // Student removed from class
							page('/logout');
							break;
					}
				} else if (!userContext.loggedIn()) {
					switch (error.code) {
						case 1063:
							model.remove('localGCAccessToken');
						case 1090:
							utilities.openModal('#class-locked-error');
							break;
						case 1091:
							page('/student-authentication');
							break;
					}
				} else if (!userContext.isStudent() && userContext.loggedIn()) {
					switch (error.code) {
						case 1063:
							model.remove('localGCAccessToken');
					}
				}
			}

			if (error.status === 409 || error.status === 500) {
				let errorMessage = ERRORS[error.code];

				// If error message is boolean false, suppress
				if (errorMessage !== false) {
					let message = GENERIC_ERROR_MESSAGE,
						timeout;

					if (typeof errorMessage === 'string') {
						message = errorMessage;
					} else if (typeof errorMessage === 'object' && errorMessage !== null) {
						message = errorMessage.message;
						timeout = errorMessage.timeout * 1000;
					}

					alertUtilities.error(message, {
						timeout: timeout,
						hasSafeHTML: true
					});
				}
			} else if (error.status === 401) {
				pubsub.publish('logout');
				alertUtilities.error('Please log in again.');
			} else if (error.status === 403) {
				alertUtilities.error('You are not authorized to view this page.');

				page('/');
			} else if (error.status === 429) {
				alertUtilities.error('You have exceeded the maximum number of attempts. Please try again later.');

				page('/');
			} else if (error.name !== 'AbortionError') {
				alertUtilities.error(GENERIC_ERROR_MESSAGE);
			}

			throw error;
		});
}

function BaseService(baseURL) {
	this.baseURL = baseURL;
}

BaseService.create = function (name, cacheSettings) {
	var service = new BaseService(name);

	BaseService._init(service, name, cacheSettings);

	return service;
};

BaseService.prototype = {
	get: function (endpoint, data) {
		if (endpoint !== null && typeof endpoint === 'object') {
			data = endpoint;
			endpoint = null;
		}

		return requester.call(this, 'get', endpoint, data);
	},

	post: function (endpoint, data) {
		if (endpoint !== null && typeof endpoint === 'object') {
			data = endpoint;
			endpoint = null;
		}

		return requester.call(this, 'post', endpoint, data);
	},

	put: function (endpoint, data) {
		if (endpoint !== null && typeof endpoint === 'object') {
			data = endpoint;
			endpoint = null;
		}

		return requester.call(this, 'put', endpoint, data);
	},

	del: function (endpoint, data) {
		if (endpoint !== null && typeof endpoint === 'object') {
			data = endpoint;
			endpoint = null;
		}

		return requester.call(this, 'del', endpoint, data);
	}
};

/**
 * Uses apiDefinitions to create all the methods on the service that correspond
 * to the endpoints.
 *
 * @param  {Object} service       the service instance
 * @param  {String} name          the name of the service in apiDefinitions
 * @param  {Object} cacheSettings optional object defining cache settings
 *
 * The cacheSettings object will contain method-by-method configuration of the cache.
 * Methods that are missing a from the object will not interact with caching.
 *
 * cacheSettings.<method-name>.clearCaches
 *                                optional array of cache names to clear
 *                                when this method is called (does not work for HTTP GET)
 *
 * cacheSettings.<method-name>.cache
 *                                string name of cache in which to store the
 *                                result of this method (GET methods only)
 *
 * cacheSettings.<method-name>.keyProperty
 *                                string name of the property in methods parameters
 *                                to use as a cache key. If absent then a default
 *                                key of "_" will be used (useful for cases where
 *                                the GET methods don't take params)
 */
BaseService._init = function (service, name, cacheSettings) {
	var definition = definitions[name],
		mapper = beatriceDataMap[name];

	cacheSettings = cacheSettings || {};
	service.definition = definition;

	Object.keys(definition).forEach(function (key) {
		var endpointDefinition = definition[key],
			endpointMapper = mapper && mapper[key];

		service[key] = function (rawParams) {
			var params;

			if (rawParams && endpointMapper && endpointMapper.clientToServer) {
				params = endpointMapper.clientToServer(rawParams);
			} else {
				params = rawParams;
			}

			var promise = service[endpointDefinition.method === 'delete' ? 'del' : endpointDefinition.method].call(this, key, params);

			if (endpointMapper && endpointMapper.serverToClient) {
				promise = promise.then(function (result) {
					return endpointMapper.serverToClient(result);
				});
			}

			if (endpointMapper && endpointMapper.serverErrorToClient) {
				promise = promise.fail(function (error) {
					throw endpointMapper.serverErrorToClient(error);
				});
			}

			return promise;
		};

		var methodConfig = cacheSettings[key];
		if (methodConfig) {
			var getCacheKey = function (data) {
				if (methodConfig.keyProperty) {
					return data[methodConfig.keyProperty];
				} else if (methodConfig.keyProperties) {
					var keyParts = [];
					methodConfig.keyProperties.forEach(function (p) {
						keyParts.push(data[p] || '_');
					});
					return keyParts.join('__');
				} else if (methodConfig.keyFunction) {
					return methodConfig.keyFunction(data);
				} else {
					return '_';
				}
			};

			// if a cacheKey setting is present for this endpoint, then wrap the endpoint
			// handler in a cache handling function
			var rawEndpointHandler = service[key].bind(service);

			if (endpointDefinition.method === 'get' && methodConfig.cache) {
				var handler = function (data) {
					var cacheKey = getCacheKey(data);
					return cache.get(methodConfig.cache, cacheKey, function () {
						return rawEndpointHandler(data);
					});
				};

				service[key] = handler;
			} else if (endpointDefinition.method !== 'get') {
				service[key] = function (data) {
					var cacheKey;
					if (methodConfig.cache) {
						cacheKey = getCacheKey(data);
					}
					return rawEndpointHandler(data).then(function (v) {
						var promises = [Q()];

						if (cacheKey) {
							promises.push(cache.invalidate(methodConfig.cache, cacheKey));
						}

						if (methodConfig.clearCaches) {
							methodConfig.clearCaches.forEach(function (c) {
								promises.push(cache.invalidateFullCache(c));
							});
						}

						return Q.all(promises).then(function () {
							return v;
						});
					});
				};
			}
		}
	});
};

module.exports = BaseService;
